import { useTranslation } from '@jq4race/i18n';
import { isPaymentDue, paymentDisplayState } from '@jq4race/shared';
import { cn } from '@jq4race/ui';
import { CheckCircle2, Clock, Loader2, PackageCheck } from 'lucide-react';

import {
  PICKUP_STATUS_LABEL_KEY,
  type PickupDisplayStatus,
} from '../../domain/value-objects/pickup-status';

interface PickupRowProps {
  ticketNumber: number;
  plate: string;
  zone: string | null;
  waitTime: string;
  status: PickupDisplayStatus;
  /** valet-keychain-qr: keychain code so the valet can locate the keys; hidden when null */
  qrTag?: string | null;
  /** Present in queue mode; absent in my-active mode */
  onClaim?: () => void;
  /** Disables the claim button and shows a spinner during mutation */
  claiming?: boolean;
  /** JQ4-26: optional deliver callback; renders "Deliver vehicle" button for in_transit rows */
  onDeliver?: () => void;
  /** JQ4-26: disables the deliver button during the mutation */
  delivering?: boolean;
  /** post-payment-delay-recharge S1: optional mark-ready callback; renders "Mark ready" button for in_transit rows */
  onMarkReady?: () => void;
  /** post-payment-delay-recharge S1: disables the mark-ready button during the mutation */
  markingReady?: boolean;
  /**
   * QA R2-03: the valet already marked the vehicle as ready (readyForPickupAt set).
   * The ticket status stays in_transit, so this renders a "Ready" chip and hides
   * the (now pointless) mark-ready button.
   */
  ready?: boolean;
  /** JQ4-39: optional payment status — renders Paid/Unpaid badge (REQ-S6-04) */
  paymentStatus?: 'unpaid' | 'paid';
  /**
   * QA R3-10: server-computed; `false` when the ticket is 'unpaid' but its total is $0.
   * Then the row shows a neutral "No charge" badge and offers no cash/comp action.
   * Absent = payment required (older server / stale offline cache).
   */
  paymentRequired?: boolean;
  /** JQ4-38: callback to open cash payment dialog — button rendered only when paymentStatus==='unpaid' */
  onRegisterCash?: () => void;
  /** JQ4-38: disables the register cash button during the mutation */
  registeringCash?: boolean;
  /** JQ4-19: callback to open comp dialog — button rendered only when compsEnabled && paymentStatus==='unpaid' */
  onApplyComp?: () => void;
  /** JQ4-19: disables the comp button during the mutation */
  applyingComp?: boolean;
  /** JQ4-19: whether comps are enabled at this location AND the actor is authorized (Scenario 5) */
  compsAuthorized?: boolean;
}

// Status metadata aligned to backend TicketStatus domain (JQ4-25 / JQ4-26)
const STATUS_CONFIG: Record<
  PickupDisplayStatus,
  { icon: typeof CheckCircle2; colorClass: string; bgClass: string }
> = {
  'in-queue': {
    icon: Clock,
    colorClass: 'text-info',
    bgClass: 'bg-info/15',
  },
  claimed: {
    icon: CheckCircle2,
    colorClass: 'text-success',
    bgClass: 'bg-success/15',
  },
  delivered: {
    icon: PackageCheck,
    colorClass: 'text-muted-foreground',
    bgClass: 'bg-muted/30',
  },
};

export function PickupRow({
  ticketNumber,
  plate,
  zone,
  waitTime,
  status,
  qrTag,
  onClaim,
  claiming = false,
  onDeliver,
  delivering = false,
  onMarkReady,
  markingReady = false,
  ready = false,
  paymentStatus,
  paymentRequired,
  onRegisterCash,
  registeringCash = false,
  onApplyComp,
  applyingComp = false,
  compsAuthorized = false,
}: PickupRowProps) {
  const { t } = useTranslation('pickup-queue');
  const { icon: StatusIcon, colorClass, bgClass } = STATUS_CONFIG[status];
  const label = t(PICKUP_STATUS_LABEL_KEY[status]);
  const paymentState = paymentDisplayState({ paymentStatus, paymentRequired });
  const paymentDue = isPaymentDue({ paymentStatus, paymentRequired });

  return (
    <article
      // QA R2-04: wrap (never overflow) — the actions row can be ~500px wide and a
      // 375px phone only has ~300px inside the card; overflow made the whole page
      // scroll sideways and pushed fixed, centred dialogs off-screen.
      className={cn(
        'flex min-h-[56px] w-full flex-wrap items-center justify-between gap-x-3 gap-y-3',
        'rounded-sm border border-border bg-card px-4 py-3.5',
      )}
      aria-label={t('row.articleAria', { ticketNumber, plate, status: label })}
    >
      {/* Left: plate (prominent) + meta */}
      <div className="flex min-w-0 flex-[1_1_8rem] flex-col gap-0.5">
        <span className="jq-mono jq-h6 text-jq-gold-bright">{plate}</span>
        <span className="jq-body-sm text-muted-foreground">
          #{ticketNumber}
          {zone ? ` · ${zone}` : ''} · {waitTime}
        </span>
        {/* valet-keychain-qr — keychain code shown only when present, no placeholder when null */}
        {qrTag ? (
          <span className="jq-mono jq-body-sm text-muted-foreground/70">{qrTag}</span>
        ) : null}
      </div>

      {/* Right: status pill + optional action buttons */}
      <div
        data-testid="pickup-row-actions"
        className="flex min-w-0 max-w-full flex-wrap items-center gap-2"
      >
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1',
            'font-sans text-[12px] font-medium leading-none tracking-snug',
            bgClass,
            colorClass,
          )}
        >
          <StatusIcon className="size-3 shrink-0" strokeWidth={2} aria-hidden />
          {label}
        </span>

        {/* QA R2-03: "Mark ready" only stamps readyForPickupAt — surface it */}
        {ready ? (
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2.5 py-1',
              'bg-success/15 text-success font-sans text-[12px] font-medium leading-none',
            )}
          >
            <PackageCheck className="size-3 shrink-0" strokeWidth={2} aria-hidden />
            {t('row.ready')}
          </span>
        ) : null}

        {/* JQ4-39: Paid/Unpaid badge (REQ-S6-04, REQ-S6-05) */}
        {paymentState ? (
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5',
              'font-sans text-[11px] font-medium leading-none',
              paymentState === 'paid' && 'bg-success/15 text-success',
              paymentState === 'due' && 'bg-warning/15 text-warning',
              // QA R3-10: unpaid but nothing owed — neutral, not a warning.
              paymentState === 'no_charge' && 'bg-muted/30 text-muted-foreground',
            )}
          >
            {paymentState === 'paid'
              ? t('row.paid')
              : paymentState === 'due'
                ? t('row.unpaid')
                : t('row.noCharge')}
          </span>
        ) : null}

        {onClaim ? (
          <button
            type="button"
            onClick={onClaim}
            disabled={claiming}
            aria-label={t('row.claimAria', { ticketNumber, plate })}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5',
              'bg-primary text-primary-foreground font-sans text-[12px] font-medium',
              'transition-opacity duration-200',
              claiming && 'cursor-not-allowed opacity-60',
            )}
          >
            {claiming ? <Loader2 className="size-3 animate-spin" aria-hidden /> : null}
            {t('row.claim')}
          </button>
        ) : null}

        {/* JQ4-26: deliver button — only when onDeliver provided AND status is "claimed" (in_transit) */}
        {onDeliver && status === 'claimed' ? (
          <button
            type="button"
            onClick={onDeliver}
            disabled={delivering}
            aria-label={t('row.deliverVehicleAria', { ticketNumber, plate })}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5',
              'bg-success text-success-foreground font-sans text-[12px] font-medium',
              'transition-opacity duration-200',
              delivering && 'cursor-not-allowed opacity-60',
            )}
          >
            {delivering ? <Loader2 className="size-3 animate-spin" aria-hidden /> : null}
            {t('row.deliverVehicle')}
          </button>
        ) : null}

        {/* post-payment-delay-recharge S1: mark ready — only when onMarkReady provided AND status is "claimed" (in_transit); hidden once ready (QA R2-03) */}
        {onMarkReady && status === 'claimed' && !ready ? (
          <button
            type="button"
            onClick={onMarkReady}
            disabled={markingReady}
            aria-label={t('row.markReadyAria', { ticketNumber, plate })}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5',
              'bg-info text-info-foreground font-sans text-[12px] font-medium',
              'transition-opacity duration-200',
              markingReady && 'cursor-not-allowed opacity-60',
            )}
          >
            {markingReady ? <Loader2 className="size-3 animate-spin" aria-hidden /> : null}
            {t('row.markReady')}
          </button>
        ) : null}

        {/* JQ4-38: register cash payment — only when paymentStatus==='unpaid' and callback provided */}
        {onRegisterCash && paymentDue ? (
          <button
            type="button"
            onClick={onRegisterCash}
            disabled={registeringCash}
            aria-label={t('row.registerCashPaymentAria', { ticketNumber, plate })}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5',
              'bg-warning text-warning-foreground font-sans text-[12px] font-medium',
              'transition-opacity duration-200',
              registeringCash && 'cursor-not-allowed opacity-60',
            )}
          >
            {registeringCash ? <Loader2 className="size-3 animate-spin" aria-hidden /> : null}
            {t('row.registerCashPayment')}
          </button>
        ) : null}

        {/* JQ4-19: apply comp (cortesía) — only when compsAuthorized && paymentStatus==='unpaid' (Scenario 5) */}
        {onApplyComp && compsAuthorized && paymentDue ? (
          <button
            type="button"
            onClick={onApplyComp}
            disabled={applyingComp}
            aria-label={t('row.applyCompAria', { ticketNumber, plate })}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5',
              'bg-info text-info-foreground font-sans text-[12px] font-medium',
              'transition-opacity duration-200',
              applyingComp && 'cursor-not-allowed opacity-60',
            )}
          >
            {applyingComp ? <Loader2 className="size-3 animate-spin" aria-hidden /> : null}
            {t('row.applyComp')}
          </button>
        ) : null}
      </div>
    </article>
  );
}
